import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

type NavigationAxis = "horizontal" | "vertical" | "both";
type WrapMode = "wrap" | "stop";

interface GroupConfig {
  container: HTMLElement;
  items: HTMLElement[];
  axis: NavigationAxis;
  wrap: WrapMode;
  activateOnFocus: boolean;
  ownerDocument: Document;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]",
  "[contenteditable]",
].join(",");

const ROLE_AXIS_MAP: Record<string, NavigationAxis> = {
  tablist: "horizontal",
  toolbar: "horizontal",
  menubar: "horizontal",
  menu: "vertical",
  listbox: "vertical",
  radiogroup: "vertical",
  group: "vertical",
  tree: "vertical",
  grid: "both",
};

const ITEM_ROLE_MAP: Record<string, string> = {
  tablist: "tab",
  toolbar: "*",
  menubar: "menuitem",
  menu: "menuitem,menuitemcheckbox,menuitemradio",
  listbox: "option",
  radiogroup: "radio",
  group: "__inputs__",
  tree: "treeitem",
  grid: "row",
};

function getNavigableItems(
  container: HTMLElement,
  containerRole: string,
): HTMLElement[] {
  const itemRoles = ITEM_ROLE_MAP[containerRole];

  if (!itemRoles) return [];

  if (itemRoles === "__inputs__") {
    return Array.from(
      container.querySelectorAll<HTMLElement>(
        "input[type='checkbox']:not([disabled]), input[type='radio']:not([disabled])",
      ),
    ).filter(
      (el) =>
        !el.closest("[aria-hidden='true']") &&
        el.closest("[style*='display: none']") === null,
    );
  }

  if (itemRoles === "*") {
    return Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.closest("[aria-hidden='true']") && !isDisabled(el));
  }

  const roleSelectors = itemRoles
    .split(",")
    .map((r) => `[role="${r.trim()}"]`)
    .join(",");

  return Array.from(
    container.querySelectorAll<HTMLElement>(roleSelectors),
  ).filter((el) => !el.closest("[aria-hidden='true']") && !isDisabled(el));
}

function isDisabled(el: HTMLElement): boolean {
  return (
    el.getAttribute("aria-disabled") === "true" ||
    (el as HTMLButtonElement).disabled === true
  );
}

function applyRovingTabindex(items: HTMLElement[], activeIndex: number): void {
  items.forEach((item, i) => {
    item.setAttribute("tabindex", i === activeIndex ? "0" : "-1");
  });
}

function getActiveIndex(items: HTMLElement[]): number {
  const idx = items.findIndex((el) => el.getAttribute("tabindex") === "0");
  return idx >= 0 ? idx : 0;
}

function resolveNextIndex(
  current: number,
  direction: 1 | -1,
  total: number,
  wrap: WrapMode,
): number {
  const next = current + direction;
  if (wrap === "wrap") {
    return (next + total) % total;
  }
  return Math.max(0, Math.min(next, total - 1));
}

export class CoreA11yKeyboard implements Plugin {
  private groups: GroupConfig[] = [];
  private keydownHandlers: Array<{
    el: HTMLElement;
    handler: (e: KeyboardEvent) => void;
  }> = [];
  private focusHandlers: Array<{
    el: HTMLElement;
    handler: (e: FocusEvent) => void;
  }> = [];
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.discoverGroups();
    this.groups.forEach((group) => this.bindGroup(group));
    this.bindEscape();
    this.bindSkipLinks();
  }

  private discoverGroups(): void {
    const roleContainers = this.element.querySelectorAll<HTMLElement>(
      Object.keys(ROLE_AXIS_MAP)
        .map((r) => `[role="${r}"]`)
        .join(","),
    );

    roleContainers.forEach((container) => {
      const role = container.getAttribute("role") as string;
      const items = getNavigableItems(container, role);
      if (items.length === 0) return;

      const axisAttr = container.dataset.a11yAxis as NavigationAxis | undefined;
      const axis = axisAttr || ROLE_AXIS_MAP[role] || "both";
      const wrap = (container.dataset.a11yWrap as WrapMode) || "wrap";
      const activateOnFocus = container.dataset.a11yActivateOnFocus === "true";

      this.groups.push({
        container,
        items,
        axis,
        wrap,
        activateOnFocus,
        ownerDocument: container.ownerDocument,
      });
    });

    const customGroups =
      this.element.querySelectorAll<HTMLElement>("[data-a11y-group]");

    customGroups.forEach((container) => {
      if (
        container.getAttribute("role") &&
        ROLE_AXIS_MAP[container.getAttribute("role")!]
      ) {
        return;
      }

      const itemSelector = container.dataset.a11yItems || FOCUSABLE_SELECTOR;
      const items = Array.from(
        container.querySelectorAll<HTMLElement>(itemSelector),
      ).filter((el) => !el.closest("[aria-hidden='true']") && !isDisabled(el));

      if (items.length === 0) return;

      const axis = (container.dataset.a11yAxis as NavigationAxis) || "vertical";
      const wrap = (container.dataset.a11yWrap as WrapMode) || "wrap";
      const activateOnFocus = container.dataset.a11yActivateOnFocus === "true";

      this.groups.push({
        container,
        items,
        axis,
        wrap,
        activateOnFocus,
        ownerDocument: container.ownerDocument,
      });
    });
  }

  private bindGroup(group: GroupConfig): void {
    const containerRole = group.container.getAttribute("role") || "";
    const isCheckboxGroup = containerRole === "group";

    if (!isCheckboxGroup) {
      applyRovingTabindex(group.items, getActiveIndex(group.items));
    }

    const keydownHandler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const currentIndex = group.items.indexOf(target);
      if (currentIndex === -1) return;

      let direction: 1 | -1 | null = null;
      let jump: "first" | "last" | null = null;

      switch (e.key) {
        case "ArrowRight":
          if (group.axis === "horizontal" || group.axis === "both") {
            direction = 1;
          }
          break;
        case "ArrowLeft":
          if (group.axis === "horizontal" || group.axis === "both") {
            direction = -1;
          }
          break;
        case "ArrowDown":
          if (group.axis === "vertical" || group.axis === "both") {
            direction = 1;
          }
          break;
        case "ArrowUp":
          if (group.axis === "vertical" || group.axis === "both") {
            direction = -1;
          }
          break;
        case "Home":
          jump = "first";
          break;
        case "End":
          jump = "last";
          break;
        case "Enter":
        case " ":
          this.activateItem(target, group);
          e.preventDefault();
          return;
        default:
          return;
      }

      e.preventDefault();

      let nextIndex: number;
      if (jump === "first") {
        nextIndex = 0;
      } else if (jump === "last") {
        nextIndex = group.items.length - 1;
      } else if (direction !== null) {
        nextIndex = resolveNextIndex(
          currentIndex,
          direction,
          group.items.length,
          group.wrap,
        );
      } else {
        return;
      }

      if (nextIndex === currentIndex) return;

      if (!isCheckboxGroup) {
        applyRovingTabindex(group.items, nextIndex);
      }
      group.items[nextIndex].focus();

      if (group.activateOnFocus) {
        this.activateItem(group.items[nextIndex], group);
      }
    };

    group.container.addEventListener(
      "keydown",
      keydownHandler as EventListener,
    );
    this.keydownHandlers.push({ el: group.container, handler: keydownHandler });

    if (!isCheckboxGroup) {
      const focusHandler = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        const idx = group.items.indexOf(target);
        if (idx !== -1) {
          applyRovingTabindex(group.items, idx);
        }
      };

      group.container.addEventListener(
        "focusin",
        focusHandler as EventListener,
      );
      this.focusHandlers.push({ el: group.container, handler: focusHandler });
    }
  }

  private activateItem(item: HTMLElement, group: GroupConfig): void {
    const role = item.getAttribute("role");
    const tagName = item.tagName.toLowerCase();

    if (tagName === "input") {
      const input = item as HTMLInputElement;
      if (input.type === "checkbox") {
        input.checked = !input.checked;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        const wrapper = input.closest(".core-checkbox");
        if (wrapper) {
          wrapper.classList.toggle("core-checkbox--selected", input.checked);
        }
        return;
      }
      if (input.type === "radio") {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }
    }

    if (role === "tab") {
      item.click();
      return;
    }

    if (role === "option") {
      const listbox = group.container;
      const prevSelected = listbox.querySelector('[aria-selected="true"]');
      if (prevSelected && prevSelected !== item) {
        prevSelected.setAttribute("aria-selected", "false");
      }
      item.setAttribute("aria-selected", "true");
      listbox.setAttribute("aria-activedescendant", item.id || "");
      group.container.dispatchEvent(
        new CustomEvent("a11y:select", {
          bubbles: true,
          detail: {
            item,
            value: item.dataset.value || item.textContent?.trim(),
          },
        }),
      );
      return;
    }

    if (
      role === "menuitem" ||
      role === "menuitemcheckbox" ||
      role === "menuitemradio"
    ) {
      item.click();
      return;
    }

    if (role === "radio") {
      const input =
        item.querySelector<HTMLInputElement>("input[type='radio']") ||
        (item as unknown as HTMLInputElement);
      if (input && "checked" in input) {
        (input as HTMLInputElement).checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }

    const nestedInput = item.querySelector<HTMLInputElement>(
      "input[type='checkbox'], input[type='radio']",
    );
    if (nestedInput && !nestedInput.disabled) {
      if (nestedInput.type === "checkbox") {
        nestedInput.checked = !nestedInput.checked;
        const wrapper = nestedInput.closest(".core-checkbox");
        if (wrapper) {
          wrapper.classList.toggle(
            "core-checkbox--selected",
            nestedInput.checked,
          );
        }
      } else {
        nestedInput.checked = true;
      }
      nestedInput.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    item.click();
  }

  private bindEscape(): void {
    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      const target = e.target as HTMLElement;

      const expandedRegion = target.closest("[aria-expanded='true']");
      if (expandedRegion) {
        const triggerId = expandedRegion.getAttribute("aria-labelledby");
        const controlledBy = expandedRegion.id
          ? this.element.querySelector<HTMLElement>(
              `[aria-controls="${expandedRegion.id}"]`,
            )
          : null;
        const trigger =
          controlledBy ||
          (triggerId
            ? this.element.querySelector<HTMLElement>(`#${triggerId}`)
            : null);

        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          trigger.focus();
          return;
        }
      }

      const popup = target.closest(
        '[role="menu"], [role="listbox"], [role="dialog"]',
      );
      if (popup) {
        const popupEl = popup as HTMLElement;
        const controlId = popupEl.id;
        const trigger = controlId
          ? this.element.querySelector<HTMLElement>(
              `[aria-controls="${controlId}"]`,
            )
          : null;

        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          trigger.focus();
        }
      }
    };

    this.element.addEventListener("keydown", this.escapeHandler);
  }

  private bindSkipLinks(): void {
    const skipLinks = this.element.querySelectorAll<HTMLAnchorElement>(
      "a[href^='#'][data-a11y-skip]",
    );

    skipLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        const targetId = link.getAttribute("href")?.slice(1);
        if (!targetId) return;

        const target = document.getElementById(targetId);
        if (!target) return;

        e.preventDefault();

        if (!target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
        }
        target.focus();
      });
    });
  }

  destroy(): void {
    this.keydownHandlers.forEach(({ el, handler }) => {
      el.removeEventListener("keydown", handler as EventListener);
    });
    this.keydownHandlers = [];

    this.focusHandlers.forEach(({ el, handler }) => {
      el.removeEventListener("focusin", handler as EventListener);
    });
    this.focusHandlers = [];

    if (this.escapeHandler) {
      this.element.removeEventListener("keydown", this.escapeHandler);
      this.escapeHandler = null;
    }

    this.groups = [];
  }
}

PluginRegistry.register("coreA11yKeyboard", CoreA11yKeyboard);
